import { useState, useMemo } from "react";
import { useTheme } from "@emotion/react";
import {
	Box,
	IconButton,
	Typography,
	FormControl,
	Select,
	MenuItem,
	Button,
	Popover,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ArrowBackIosSharp, ArrowForwardIosSharp } from "@mui/icons-material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";

function ChangePeriodRow({
	period,
	setPeriod,
	periodType,
	setPeriodType,
	periodTypeOptions,
}) {
	const { t } = useTranslation();
	const theme = useTheme();

	const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
	const [tempDate, setTempDate] = useState(dayjs(period));

	const now = new Date();
	const today = new Date(
		Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
	);

	const minDate = new Date(Date.UTC(2000, 0, 1));

	const periodDate = new Date(period);

	const isToday =
		periodDate.toISOString().slice(0, 10) ===
		today.toISOString().slice(0, 10);

	const isMinDate =
		periodDate.getUTCFullYear() === minDate.getUTCFullYear() &&
		periodDate.getUTCMonth() === minDate.getUTCMonth() &&
		periodDate.getUTCDate() === minDate.getUTCDate();

	const { dateOptions, calendarViews, openTo } = useMemo(() => {
		switch (periodType) {
			case "Daily":
				return {
					dateOptions: {
						year: "numeric",
						month: "long",
						day: "numeric",
					},
					calendarViews: ["year", "month", "day"],
					openTo: "day",
				};
			case "Monthly":
				return {
					dateOptions: { year: "numeric", month: "long" },
					calendarViews: ["year", "month"],
					openTo: "month",
				};
			case "Yearly":
				return {
					dateOptions: { year: "numeric" },
					calendarViews: ["year"],
					openTo: "year",
				};
			default:
				return {
					dateOptions: {
						year: "numeric",
						month: "long",
						day: "numeric",
					},
					calendarViews: ["year", "month", "day"],
					openTo: "day",
				};
		}
	}, [periodType]);

	function capitalizeFirstLetter(string) {
		return string.charAt(0).toUpperCase() + string.slice(1);
	}

	function getMonthlyPeriod(baseDate, direction) {
		const today = new Date();
		today.setUTCHours(0, 0, 0, 0);

		const newMonth = baseDate.getUTCMonth() + direction;
		const newYear = baseDate.getUTCFullYear();

		const candidate = new Date(Date.UTC(newYear, newMonth, 1));

		if (
			candidate.getUTCMonth() === today.getUTCMonth() &&
			candidate.getUTCFullYear() === today.getUTCFullYear()
		) {
			return today;
		}
		return new Date(
			Date.UTC(candidate.getUTCFullYear(), candidate.getUTCMonth() + 1, 0)
		);
	}

	function setNextPeriod() {
		let newDate;
		if (periodType === "Monthly") {
			newDate = getMonthlyPeriod(periodDate, 1);
		} else if (periodType === "Daily") {
			newDate = new Date(
				Date.UTC(
					periodDate.getUTCFullYear(),
					periodDate.getUTCMonth(),
					periodDate.getUTCDate() + 1
				)
			);
		} else if (periodType === "Yearly") {
			newDate = new Date(
				Date.UTC(
					periodDate.getUTCFullYear() + 1,
					periodDate.getUTCMonth(),
					periodDate.getUTCDate()
				)
			);
		}
		newDate.setUTCHours(0, 0, 0, 0);
		setPeriod(newDate.toISOString());
	}

	function setPreviousPeriod() {
		let newDate;
		if (periodType === "Monthly") {
			newDate = getMonthlyPeriod(periodDate, -1);
		} else if (periodType === "Daily") {
			newDate = new Date(
				Date.UTC(
					periodDate.getUTCFullYear(),
					periodDate.getUTCMonth(),
					periodDate.getUTCDate() - 1
				)
			);
		} else {
			newDate = new Date(
				Date.UTC(
					periodDate.getUTCFullYear() - 1,
					periodDate.getUTCMonth(),
					periodDate.getUTCDate()
				)
			);
		}
		newDate.setUTCHours(0, 0, 0, 0);
		setPeriod(newDate.toISOString());
	}

	const topRowBoxStyles = {
		display: "flex",
		justifyContent: "space-between",
		flexWrap: "wrap",
		mx: 2,
		gap: 2,
	};
	const periodBoxStyles = {
		display: "flex",
		bgcolor: theme.palette.background.paper,
		borderRadius: "10px",
		alignItems: "center",
		justifyContent: "space-between",
		p: 1,
	};

	const selectStyles = {
		typography: "h6",
		backgroundColor: theme.palette.background.paper,
		"& fieldset": { border: "none" },
		borderRadius: "10px",
		p: 1,
	};

	const dateCalendarStyles = {
		height: "auto !important",
		maxHeight: "none !important",
		"& .Mui-selected": {
			backgroundColor: theme.palette.active.main + " !important",
		},
		"& .Mui-selected:hover": {
			backgroundColor: theme.palette.active.main + " !important",
		},
	};

	const userLanguage = localStorage.getItem("i18nextLng") || "en";

	const periodName = capitalizeFirstLetter(
		new Date(period).toLocaleDateString(userLanguage, dateOptions)
	);

	const changePeriod = (e) => {
		setPeriodType(e.target.value);
	};

	return (
		<>
			<Box sx={topRowBoxStyles}>
				<Box sx={periodBoxStyles}>
					<IconButton
						aria-label="previous"
						onClick={setPreviousPeriod}
						disabled={isMinDate}
					>
						<ArrowBackIosSharp />
					</IconButton>
					<Button
						variant="text"
						onClick={(e) => setCalendarAnchorEl(e.currentTarget)}
					>
						<Typography
							variant="h6"
							id="tableTitle"
							component="div"
							sx={{ marginBottom: 0 }}
						>
							{periodName}
						</Typography>
					</Button>

					<IconButton
						aria-label="next"
						onClick={setNextPeriod}
						disabled={isToday}
					>
						<ArrowForwardIosSharp />
					</IconButton>
				</Box>
				<FormControl>
					<Select
						value={periodType}
						onChange={changePeriod}
						displayEmpty
						inputProps={{ "aria-label": "Without label" }}
						sx={selectStyles}
					>
						{periodTypeOptions && periodTypeOptions.length > 0
							? periodTypeOptions.map((option) => (
									<MenuItem key={option} value={option}>
										{t(option.toLowerCase())}
									</MenuItem>
								))
							: null}
					</Select>
				</FormControl>
			</Box>
			<Popover
				disableEnforceFocus
				open={Boolean(calendarAnchorEl)}
				anchorEl={calendarAnchorEl}
				onClose={() => setCalendarAnchorEl(null)}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
				PaperProps={{
					sx: {
						borderRadius: "10px",
					},
				}}
			>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<DateCalendar
						value={tempDate}
						onChange={(newValue) => setTempDate(newValue)}
						referenceDate={dayjs(period)}
						views={calendarViews}
						openTo={openTo}
						sx={dateCalendarStyles}
						minDate={dayjs(minDate)}
						maxDate={dayjs(today)}
					/>
					<Box
						sx={{
							m: 2,
							display: "flex",
							justifyContent: "flex-end",
						}}
					>
						<Button
							variant="contained"
							onClick={() => {
								setPeriod(
									tempDate.startOf("day").toISOString()
								);
								setCalendarAnchorEl(null);
							}}
						>
							{t("save")}
						</Button>
					</Box>
				</LocalizationProvider>
			</Popover>
		</>
	);
}

export default ChangePeriodRow;
